import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
	Camera as CameraIcon,
	X,
	RotateCw,
	Circle,
	CheckCircle,
	Video,
	Mic,
	Undo2,
	Volume2,
	VolumeX,
	Play,
	Pause,
	Star,
	Award,
	Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Waveform from "@/components/Waveform";
import { IMAGE_PATH } from "../api/apiConfig";

/**
 * Custom Camera Component
 * Provides full control over camera UI and functionality
 *
 * @param {Object} props
 * @param {Function} props.onCapture - Callback when photo is captured (receives File object)
 * @param {Function} props.onClose - Callback when camera is closed
 * @param {string} props.mode - 'photo' or 'video' (default: 'photo')
 * @param {boolean} props.showPreview - Show preview after capture (default: true)
 * @param {boolean} props.autoTriggerUpload - Automatically trigger file upload on mount (default: false)
 * @param {string} props.postType - Type of post being created ('glow', 'pride', 'grateful') - shows suggestions button only for 'glow'
 * @param {boolean} props.isContest - Whether this is for a contest submission (default: false)
 * @param {boolean} props.delayInitialization - Delay camera initialization until component is ready (default: false)
 */
const Camera = ({
	onCapture,
	onClose,
	mode: initialMode = "photo",
	showPreview = true,
	autoTriggerUpload = false,
	postType = null,
	isContest = false,
	delayInitialization = false,
}) => {
	const videoRef = useRef(null);
	const previewVideoRef = useRef(null);
	const canvasRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);
	const audioRecorderRef = useRef(null);
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const animationFrameRef = useRef(null);
	const fileInputRef = useRef(null);
	const videoInputRef = useRef(null);
	const audioInputRef = useRef(null);
	const streamRef = useRef(null); // Track stream in ref to avoid dependency issues
	const audioElementRef = useRef(null); // For audio playback
	const playbackAnalyserRef = useRef(null); // Separate analyser for playback

	const [stream, setStream] = useState(null);
	const [capturedImage, setCapturedImage] = useState(null);
	const [capturedVideo, setCapturedVideo] = useState(null);
	const [isRecording, setIsRecording] = useState(false);
	const [recordingDuration, setRecordingDuration] = useState(0);
	// Set default facingMode based on device type:
	// Desktop: always front-facing camera (user)
	// Mobile: back camera (environment)
	// Desktop always defaults to front, but mobile can use saved preference
	const [facingMode, setFacingMode] = useState(() => {
		const isMobile =
			/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
			window.innerWidth < 768;

		if (isMobile) {
			// Mobile: check localStorage for preference, default to back camera
			const saved = localStorage.getItem("cameraFacingMode");
			return saved === "user" ? "user" : "environment";
		}
		// Desktop: always default to front-facing camera (user)
		return "user";
	});
	const [error, setError] = useState(null);
	const [hasPermission, setHasPermission] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);
	const [mode, setMode] = useState(initialMode); // Allow mode switching
	const [showCrop, setShowCrop] = useState(false);
	const [imageToCrop, setImageToCrop] = useState(null);
	const [crop, setCrop] = useState(null);
	const [completedCrop, setCompletedCrop] = useState(null);
	const imgRef = useRef(null);
	const [isAudioRecording, setIsAudioRecording] = useState(false);
	const [audioLevels, setAudioLevels] = useState([]);
	const [capturedAudio, setCapturedAudio] = useState(null);
	const [isMuted, setIsMuted] = useState(false);
	const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
	const [isAudioPlaying, setIsAudioPlaying] = useState(false);
	const [audioDuration, setAudioDuration] = useState(0);
	const [audioCurrentTime, setAudioCurrentTime] = useState(0);
	const [floatingSuggestions, setFloatingSuggestions] = useState([]);
	const [floatingSpecs, setFloatingSpecs] = useState([]);
	const [isUploadedMedia, setIsUploadedMedia] = useState(false); // Track if media was uploaded vs captured
	const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track if user has interacted

	// Timer for recording duration
	useEffect(() => {
		let interval;
		if (isRecording || isAudioRecording) {
			interval = setInterval(() => {
				setRecordingDuration((prev) => prev + 1);
			}, 1000);
		} else {
			setRecordingDuration(0);
		}
		return () => clearInterval(interval);
	}, [isRecording, isAudioRecording]);

	// Save facingMode to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem("cameraFacingMode", facingMode);
	}, [facingMode]);

	// Check for multiple cameras - only when camera is actually being used (photo/video mode)
	// Don't check on mount to avoid any potential permission prompts before user selects a method
	useEffect(() => {
		// Only check cameras when we're actually using camera (photo/video mode) and not delaying initialization
		if (delayInitialization || autoTriggerUpload || mode === "audio") {
			return;
		}

		const checkCameras = async () => {
			try {
				const devices = await navigator.mediaDevices.enumerateDevices();
				const videoDevices = devices.filter(
					(device) => device.kind === "videoinput"
				);

				// Check if we have cameras with different facing modes
				const hasFrontCamera = videoDevices.some(
					(device) =>
						device.label.toLowerCase().includes("front") ||
						device.label.toLowerCase().includes("user")
				);
				const hasBackCamera = videoDevices.some(
					(device) =>
						device.label.toLowerCase().includes("back") ||
						device.label.toLowerCase().includes("rear") ||
						device.label.toLowerCase().includes("environment")
				);

				// If we have at least 2 video devices, assume we have both front and back
				setHasMultipleCameras(
					videoDevices.length > 1 || (hasFrontCamera && hasBackCamera)
				);

				console.log("Camera detection:", {
					totalCameras: videoDevices.length,
					hasFrontCamera,
					hasBackCamera,
					hasMultipleCameras: videoDevices.length > 1,
				});
			} catch (error) {
				console.error("Error checking cameras:", error);
				// Default to showing flip button on mobile if we can't detect
				setHasMultipleCameras(
					/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
				);
			}
		};

		checkCameras();
	}, [mode, delayInitialization, autoTriggerUpload]);

	// Hide navbar when camera is active
	useEffect(() => {
		// Add class to body to hide navbar
		document.body.classList.add("camera-active");

		return () => {
			// Remove class when camera closes
			document.body.classList.remove("camera-active");
		};
	}, []);

	// Initialize camera - restart when facingMode or mode changes
	// Skip camera initialization if we're in upload mode or audio mode
	// Also skip if delayInitialization is true (wait for explicit user action)
	useEffect(() => {
		if (delayInitialization) {
			// Don't initialize yet - wait for user to explicitly open camera
			setIsInitializing(false);
			setHasPermission(false);
			return;
		}

		if (autoTriggerUpload) {
			// In upload mode, skip camera initialization and mark as ready
			setIsInitializing(false);
			setHasPermission(true);
		} else if (mode === "audio") {
			// In audio mode, skip camera initialization
			// Microphone will be requested when user clicks record button
			setIsInitializing(false);
			setHasPermission(true); // Mark as ready (microphone permission will be requested on record)
		} else {
			// For photo/video modes, initialize camera
			startCamera();
		}
		return () => {
			if (!autoTriggerUpload && mode !== "audio" && !delayInitialization) {
				stopCamera();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [facingMode, mode, autoTriggerUpload, delayInitialization]); // startCamera is stable due to useCallback with these dependencies

	// Ensure video element transform updates when facingMode changes
	useEffect(() => {
		if (videoRef.current && streamRef.current) {
			// Force update of transform style
			videoRef.current.style.transform =
				facingMode === "user" ? "scaleX(-1)" : "none";
		}
	}, [facingMode]);

	// Note: autoTriggerUpload now shows upload placeholder instead of auto-clicking
	// The user manually clicks "Choose File" button on the placeholder screen

	// Ensure video plays when stream is set OR when video element mounts
	useEffect(() => {
		if (
			videoRef.current &&
			streamRef.current &&
			hasPermission &&
			!isInitializing
		) {
			const video = videoRef.current;
			const stream = streamRef.current;

			// Ensure stream is attached to video
			if (video.srcObject !== stream) {
				console.log("🔗 Attaching stream to video element via useEffect");
				video.srcObject = stream;
				// Apply transform
				video.style.transform = facingMode === "user" ? "scaleX(-1)" : "none";
			}

			// Ensure video properties are set
			video.muted = true;
			video.playsInline = true;
			video.autoplay = true;

			// Detect when video stops playing unexpectedly (camera disconnected)
			const handleVideoPause = () => {
				// Check if video paused unexpectedly (not user-initiated)
				if (video.paused && streamRef.current) {
					const videoTracks = streamRef.current.getVideoTracks();
					const activeTracks = videoTracks.filter(
						(track) => track.readyState === "live"
					);

					// If no active video tracks, camera was disconnected
					if (activeTracks.length === 0 && videoTracks.length > 0) {
						console.warn("Video paused - camera may be disconnected");
						setError(
							"Camera connection lost. Please check your camera connection and try again."
						);
						setHasPermission(false);
					}
				}
			};

			// Listen for video pause events
			video.addEventListener("pause", handleVideoPause);

			// Cleanup: remove event listener when component unmounts or stream changes
			return () => {
				video.removeEventListener("pause", handleVideoPause);
			};

			// Try to play the video
			const tryPlay = async () => {
				try {
					if (video.paused) {
						console.log(
							"▶️ Attempting to play video, readyState:",
							video.readyState
						);
						await video.play();
						console.log("✅ Camera video playing via useEffect");
					} else {
						console.log("✅ Camera video already playing");
					}
				} catch (error) {
					console.warn("❌ Error playing video in useEffect:", error);
					// Retry after a short delay
					setTimeout(async () => {
						try {
							if (video.paused) {
								await video.play();
								console.log("✅ Camera video playing via useEffect (retry)");
							}
						} catch (retryError) {
							console.warn("❌ Retry play error:", retryError);
						}
					}, 200);
				}
			};

			// If video is already ready, play immediately
			if (video.readyState >= 2) {
				tryPlay();
			} else {
				// Wait for video to be ready
				const onLoadedMetadata = () => {
					console.log(
						"📹 Video metadata loaded, readyState:",
						video.readyState,
						"attempting to play"
					);
					tryPlay();
					video.removeEventListener("loadedmetadata", onLoadedMetadata);
				};
				video.addEventListener("loadedmetadata", onLoadedMetadata);

				// Also try after a timeout
				setTimeout(() => {
					console.log(
						"⏱️ Timeout: attempting to play video, readyState:",
						video.readyState
					);
					tryPlay();
				}, 500);
			}
		} else {
			if (!videoRef.current) {
				console.log("⚠️ videoRef.current is null");
			}
			if (!streamRef.current) {
				console.log("⚠️ streamRef.current is null");
			}
			if (!hasPermission) {
				console.log("⚠️ hasPermission is false");
			}
			if (isInitializing) {
				console.log("⚠️ isInitializing is true");
			}
		}
	}, [stream, hasPermission, isInitializing, facingMode]);

	const startCamera = useCallback(async () => {
		// Don't start camera if we're in audio mode
		if (mode === "audio") {
			console.log("Skipping camera initialization for audio mode");
			return;
		}

		try {
			setError(null);
			setIsInitializing(true);

			// Stop existing stream if any
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
			}

			// Use minimal constraints for fastest initialization
			// Start with basic constraints - browser will use best available quickly
			const constraints = {
				video: {
					facingMode,
					// Minimal constraints for fastest startup
					// Browser will use the best available resolution
				},
				audio: mode === "video",
			};

			// Skip permission check - getUserMedia will handle it and is faster
			// Permission check adds unnecessary delay and getUserMedia will prompt anyway

			const mediaStream =
				await navigator.mediaDevices.getUserMedia(constraints);
			streamRef.current = mediaStream;
			setStream(mediaStream);
			setHasPermission(true);

			// Add event listeners to detect when camera/microphone is disconnected
			const handleTrackEnded = (event) => {
				const track = event.target;
				console.warn(
					"Media track ended unexpectedly:",
					track.kind,
					track.label
				);

				if (track.kind === "video") {
					setError(
						"Camera connection lost. Please check your camera connection and try again."
					);
					setHasPermission(false);
					// Stop all tracks
					if (streamRef.current) {
						streamRef.current.getTracks().forEach((t) => t.stop());
						streamRef.current = null;
						setStream(null);
					}
				} else if (track.kind === "audio") {
					if (isRecording || isAudioRecording) {
						// If recording, stop it and show error
						if (isRecording) {
							stopRecording();
						}
						if (isAudioRecording) {
							stopAudioRecording();
						}
						setError(
							"Microphone connection lost during recording. Please check your microphone connection and try again."
						);
					} else {
						setError(
							"Microphone connection lost. Please check your microphone connection and try again."
						);
					}
					setHasPermission(false);
				}
			};

			// Add ended listeners to all tracks
			mediaStream.getTracks().forEach((track) => {
				track.addEventListener("ended", handleTrackEnded);
			});

			// Periodic check for track state (in case 'ended' event doesn't fire reliably)
			const trackCheckInterval = setInterval(() => {
				if (!streamRef.current) {
					clearInterval(trackCheckInterval);
					return;
				}

				const tracks = streamRef.current.getTracks();
				const videoTracks = tracks.filter((t) => t.kind === "video");
				const audioTracks = tracks.filter((t) => t.kind === "audio");

				// Check video tracks
				if (videoTracks.length > 0) {
					const activeVideoTracks = videoTracks.filter(
						(t) => t.readyState === "live"
					);
					if (activeVideoTracks.length === 0) {
						console.warn("Video tracks became inactive - camera disconnected");
						clearInterval(trackCheckInterval);
						handleTrackEnded({ target: videoTracks[0] });
					}
				}

				// Check audio tracks (only if in video mode)
				if (mode === "video" && audioTracks.length > 0) {
					const activeAudioTracks = audioTracks.filter(
						(t) => t.readyState === "live"
					);
					if (
						activeAudioTracks.length === 0 &&
						(isRecording || isAudioRecording)
					) {
						console.warn("Audio tracks became inactive during recording");
						clearInterval(trackCheckInterval);
						handleTrackEnded({ target: audioTracks[0] });
					}
				}
			}, 2000); // Check every 2 seconds

			// Store interval ID for cleanup
			if (!window.cameraTrackCheckIntervals) {
				window.cameraTrackCheckIntervals = [];
			}
			window.cameraTrackCheckIntervals.push(trackCheckInterval);

			if (videoRef.current) {
				const video = videoRef.current;
				console.log(
					"Setting stream on video element, stream tracks:",
					mediaStream.getVideoTracks().length
				);
				video.srcObject = mediaStream;
				// Apply transform for front-facing camera
				video.style.transform = facingMode === "user" ? "scaleX(-1)" : "none";

				// Ensure video is set up for autoplay
				video.muted = true;
				video.playsInline = true;
				video.autoplay = true;

				// Wait for video to be ready, then play
				const playVideo = async () => {
					try {
						console.log(
							"Attempting to play video, readyState:",
							video.readyState,
							"paused:",
							video.paused
						);
						// Wait a bit for the stream to be ready
						if (video.readyState >= 2) {
							await video.play();
							console.log("✅ Camera video started playing");
						} else {
							// Wait for loadedmetadata event
							const onLoadedMetadata = async () => {
								try {
									console.log("Metadata loaded, readyState:", video.readyState);
									await video.play();
									console.log(
										"✅ Camera video started playing (after metadata)"
									);
								} catch (err) {
									console.warn("❌ Error playing video after metadata:", err);
								}
								video.removeEventListener("loadedmetadata", onLoadedMetadata);
							};
							video.addEventListener("loadedmetadata", onLoadedMetadata);

							// Also try after a delay
							setTimeout(async () => {
								if (video.readyState >= 2 && video.paused) {
									try {
										await video.play();
										console.log("✅ Camera video started playing (delayed)");
									} catch (err) {
										console.warn("❌ Error playing video (delayed):", err);
									}
								}
							}, 300);
						}
					} catch (playError) {
						console.warn("❌ Video play error:", playError);
						// Try again after a short delay
						setTimeout(async () => {
							try {
								if (video.paused) {
									await video.play();
									console.log("✅ Camera video started playing (retry)");
								}
							} catch (retryError) {
								console.warn("❌ Video play retry error:", retryError);
							}
						}, 200);
					}
				};

				playVideo();
			} else {
				console.warn("⚠️ videoRef.current is null, cannot set stream");
			}

			// Mark as initialized immediately after stream is set
			// The video will start playing automatically once the stream is ready
			setIsInitializing(false);
		} catch (err) {
			console.error("Error accessing camera:", err);
			const errorName = err?.name || err?.constructor?.name || "UnknownError";
			if (
				errorName === "NotAllowedError" ||
				errorName === "PermissionDeniedError"
			) {
				setError(
					"Camera access was denied. Please allow camera permissions in your browser settings and try again."
				);
			} else if (
				errorName === "NotFoundError" ||
				errorName === "DevicesNotFoundError"
			) {
				setError("No camera found. Please connect a camera and try again.");
			} else if (
				errorName === "NotReadableError" ||
				errorName === "TrackStartError"
			) {
				setError(
					"Camera is already in use by another application. Please close other apps using the camera and try again."
				);
			} else {
				setError(
					"Unable to access camera. Please check your browser permissions and try again."
				);
			}
			setHasPermission(false);
			setIsInitializing(false);
		}
	}, [facingMode, mode]);

	const stopCamera = () => {
		// Clear track check intervals
		if (window.cameraTrackCheckIntervals) {
			window.cameraTrackCheckIntervals.forEach((interval) =>
				clearInterval(interval)
			);
			window.cameraTrackCheckIntervals = [];
		}

		// Remove video pause listener
		if (videoRef.current) {
			// Clone the video element's event listeners list is not directly accessible
			// But we can remove by replacing the srcObject which will clean up
			videoRef.current.srcObject = null;
		}

		if (streamRef.current) {
			// Remove event listeners before stopping tracks
			streamRef.current.getTracks().forEach((track) => {
				// Remove all event listeners by cloning the track's event listeners
				// (we can't directly remove, but stopping the track will clean it up)
				track.stop();
			});
			streamRef.current = null;
			setStream(null);
		}
		// Clean up animation frames
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
	};

	const switchCamera = () => {
		setFacingMode((prev) => {
			const newMode = prev === "user" ? "environment" : "user";
			// Save to localStorage immediately
			localStorage.setItem("cameraFacingMode", newMode);
			return newMode;
		});
	};

	// Example suggestions - different based on post type
	const glowSuggestions = [
		"Give a compliment to another person",
		"Teach someone a special skill free of charge",
		"Organize a food drive",
		"Help a neighbor with their groceries",
		"Volunteer at a local charity",
		"Write a thank you note to someone special",
		"Pay for someone's coffee behind you",
		"Mentor someone in your field",
		"Clean up a public space",
		"Call an old friend you haven't talked to in a while",
		"Leave a kind note for a coworker",
		"Donate clothes you no longer wear",
		"Hold the door open with a smile",
		"Share your umbrella with someone in the rain",
		"Bring treats to your local fire station",
		"Water a neighbor's plants while they're away",
		"Send a care package to someone far away",
		"Give someone a ride who needs it",
		"Babysit for free for a stressed parent",
		"Teach an elderly person how to use technology",
		"Leave a generous tip for great service",
		"Pick up litter you see on your walk",
		"Donate blood at a local blood bank",
		"Compliment someone's work publicly",
		"Share your skills at a community center",
		"Surprise someone with their favorite treat",
		"Help someone carry heavy items",
		"Write a positive review for a small business",
		"Offer to walk a neighbor's dog",
		"Send flowers to someone who needs cheering up",
		"Return shopping carts in the parking lot",
		"Give up your seat on public transportation",
		"Invite someone new to join your plans",
		"Make a meal for someone who is struggling",
		"Leave coins at a laundromat for others to use",
		"Recommend a friend for a job opportunity",
		"Share useful information you've learned",
		"Plant a tree in your community",
		"Pay someone's toll on the highway",
		"Offer to help someone move",
	];

	const gratefulSuggestions = [
		"Who are you grateful for?",
		"What are you grateful for?",
		"What made you smile today?",
		"Who inspires you?",
		"What blessing are you thankful for?",
		"What moment brought you joy?",
		"Who has helped you recently?",
		"What do you appreciate about your life?",
	];

	// Use the appropriate suggestion list based on post type
	const getSuggestionList = () => {
		return postType === "grateful" ? gratefulSuggestions : glowSuggestions;
	};

	// Auto-spawn floating suggestions for glow and grateful posts (but not for contests)
	useEffect(() => {
		if (
			(postType !== "glow" && postType !== "grateful") ||
			isContest || // Don't show suggestions for contest submissions
			capturedImage ||
			capturedVideo ||
			capturedAudio ||
			showCrop ||
			autoTriggerUpload ||
			hasUserInteracted
		) {
			return;
		}

		// Different spawn rates for different post types
		const isGrateful = postType === "grateful";
		const minInterval = isGrateful ? 10000 : 2000; // 10s for grateful, 2s for glow
		const maxInterval = isGrateful ? 10000 : 3000; // 10s for grateful, 3s for glow

		// Spawn suggestions automatically
		const spawnInterval = setInterval(
			() => {
				const suggestionList = getSuggestionList();
				const randomSuggestion =
					suggestionList[Math.floor(Math.random() * suggestionList.length)];
				const newSuggestion = {
					id: Date.now() + Math.random(),
					text: randomSuggestion,
					left: Math.random() * 100, // Random position between 0% and 100% for full coverage
					isGrateful: postType === "grateful", // Track if this is a grateful suggestion
				};

				setFloatingSuggestions((prev) => [...prev, newSuggestion]);

				// Remove suggestion after animation completes
				setTimeout(() => {
					setFloatingSuggestions((prev) =>
						prev.filter((s) => s.id !== newSuggestion.id)
					);
				}, 15000); // 15 seconds to match animation duration
			},
			Math.random() * (maxInterval - minInterval) + minInterval
		); // Random interval based on post type

		return () => clearInterval(spawnInterval);
	}, [
		postType,
		isContest,
		capturedImage,
		capturedVideo,
		capturedAudio,
		showCrop,
		autoTriggerUpload,
		hasUserInteracted,
	]);

	// Auto-spawn floating orange specs
	useEffect(() => {
		if (
			capturedImage ||
			capturedVideo ||
			capturedAudio ||
			showCrop ||
			autoTriggerUpload ||
			hasUserInteracted
		) {
			return;
		}

		// Spawn colored specs frequently (blue+orange for contest, green for grateful, orange for glow)
		const specInterval = setInterval(() => {
			const getSpecColor = () => {
				if (isContest) {
					// For contests, randomly choose between blue and orange
					return Math.random() > 0.5 ? "blue" : "orange";
				}
				return postType === "grateful" ? "green" : "orange";
			};

			const newSpec = {
				id: Date.now() + Math.random(),
				left: Math.random() * 100, // Random position across entire width
				size: Math.random() * 6 + 4, // Random size between 4-10px
				delay: Math.random() * 2, // Random delay for staggered effect
				color: getSpecColor(),
			};

			setFloatingSpecs((prev) => [...prev, newSpec]);

			// Remove spec after animation completes
			setTimeout(() => {
				setFloatingSpecs((prev) => prev.filter((s) => s.id !== newSpec.id));
			}, 12000); // 12 seconds to match animation duration
		}, 500); // Spawn a spec every 500ms

		return () => clearInterval(specInterval);
	}, [
		isContest,
		postType,
		capturedImage,
		capturedVideo,
		capturedAudio,
		showCrop,
		autoTriggerUpload,
		hasUserInteracted,
	]);

	// Clear all floating elements when user interacts
	useEffect(() => {
		if (hasUserInteracted) {
			// Wait for fade animation to complete before clearing
			setTimeout(() => {
				setFloatingSuggestions([]);
				setFloatingSpecs([]);
			}, 500); // Match the animation duration
		}
	}, [hasUserInteracted]);

	// Handle any user interaction (touch or click)
	const handleUserInteraction = () => {
		if (!hasUserInteracted) {
			setHasUserInteracted(true);
		}
	};

	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) {
			return;
		}
		if (!videoRef.current || !canvasRef.current) {
			return;
		}

		const video = videoRef.current;
		const canvas = canvasRef.current;

		// Always crop to 9:16 (mobile aspect ratio) first - never show full screen version
		const targetWidth = 480;
		const targetHeight = 854; // 480 * 16/9
		canvas.width = targetWidth;
		canvas.height = targetHeight;

		const ctx = canvas.getContext("2d");

		// Calculate crop dimensions to get center crop at 9:16
		const videoAspect = video.videoWidth / video.videoHeight;
		const targetAspect = 9 / 16;

		let sourceWidth, sourceHeight, sourceX, sourceY;

		if (videoAspect > targetAspect) {
			// Video is wider - crop sides
			sourceHeight = video.videoHeight;
			sourceWidth = sourceHeight * targetAspect;
			sourceX = (video.videoWidth - sourceWidth) / 2;
			sourceY = 0;
		} else {
			// Video is taller - crop top/bottom
			sourceWidth = video.videoWidth;
			sourceHeight = sourceWidth / targetAspect;
			sourceX = 0;
			sourceY = (video.videoHeight - sourceHeight) / 2;
		}

		// Draw cropped video frame to canvas
		// Flip horizontally for front-facing camera (mirror view)
		if (facingMode === "user") {
			ctx.save();
			ctx.translate(targetWidth, 0);
			ctx.scale(-1, 1);
		}

		ctx.drawImage(
			video,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			targetWidth,
			targetHeight
		);

		// Reset transform for front-facing camera
		if (facingMode === "user") {
			ctx.restore();
		}

		// Convert canvas to blob and show crop interface
		// User will then crop this 9:16 version to a square
		canvas.toBlob(
			(blob) => {
				const imageUrl = URL.createObjectURL(blob);
				setImageToCrop(imageUrl);
				setShowCrop(true);
				setIsUploadedMedia(false); // Mark as camera-captured
				// Don't set crop here - let onImageLoaded handle it based on actual displayed dimensions
				// This ensures proper centering based on how the image is actually displayed
			},
			"image/jpeg",
			0.95
		);
	};

	const onCropChange = (newCrop) => {
		setCrop(newCrop);
	};

	const onCropComplete = (newCrop) => {
		setCrop(newCrop);
		setCompletedCrop(newCrop);
	};

	const onImageLoaded = useCallback(
		(e) => {
			const img = e.currentTarget;
			if (img && !crop) {
				// Function to calculate and set crop based on actual displayed dimensions
				const calculateCrop = () => {
					// Get the actual displayed dimensions of the image
					// ReactCrop uses the displayed size, not natural size
					const displayedWidth =
						img.offsetWidth || img.clientWidth || img.width || 0;
					const displayedHeight =
						img.offsetHeight || img.clientHeight || img.height || 0;

					// If dimensions aren't available yet, wait a bit more
					if (displayedWidth === 0 || displayedHeight === 0) {
						setTimeout(calculateCrop, 50);
						return;
					}

					// Crop size should NEVER exceed the width of the image
					// Since it's a square crop, use the smaller of width or height
					// This ensures the crop fits completely within the image
					const cropSize = Math.min(displayedWidth, displayedHeight);

					// Center the crop on the image
					const initialX = Math.max(0, (displayedWidth - cropSize) / 2);
					const initialY = Math.max(0, (displayedHeight - cropSize) / 2);

					setCrop({
						unit: "px",
						x: initialX,
						y: initialY,
						width: cropSize,
						height: cropSize,
					});
				};

				// Wait for image to be fully rendered before calculating crop
				// Use requestAnimationFrame to ensure layout is complete
				requestAnimationFrame(() => {
					setTimeout(calculateCrop, 50);
				});
			}
		},
		[crop]
	);

	const applyCrop = async () => {
		if (!imageToCrop || !crop || !imgRef.current) {
			return;
		}

		try {
			const croppedImageUrl = await getCroppedImg(imgRef.current, crop);
			setCapturedImage(croppedImageUrl);
			setShowCrop(false);

			// Clear crop state after applying
			setCrop(null);

			if (!showPreview) {
				const response = await fetch(croppedImageUrl);
				const blob = await response.blob();
				const file = new File([blob], `photo-${Date.now()}.jpg`, {
					type: "image/jpeg",
				});
				onCapture(file);
				handleClose();
			}
		} catch (error) {
			console.error("Error applying crop:", error);
			setError("Failed to apply crop. Please try again.");
		}
	};

	const handleUndo = () => {
		// Revoke the current captured image URL to free memory
		if (capturedImage) {
			URL.revokeObjectURL(capturedImage);
		}
		// Clear captured image and show crop screen again
		setCapturedImage(null);
		setShowCrop(true);
		// Reset crop to null so it will be recalculated on image load
		setCrop(null);
	};

	const getCroppedImg = (image, crop) => {
		if (!crop || !image) {
			return Promise.reject(new Error("Invalid crop or image"));
		}

		return new Promise((resolve, reject) => {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			const scaleX = image.naturalWidth / image.width;
			const scaleY = image.naturalHeight / image.height;

			// Square crop should be 480x480 (same width as phone crop)
			const targetSize = 480;
			canvas.width = targetSize;
			canvas.height = targetSize;

			const pixelCrop = {
				x: crop.x * scaleX,
				y: crop.y * scaleY,
				width: crop.width * scaleX,
				height: crop.height * scaleY,
			};

			ctx.drawImage(
				image,
				pixelCrop.x,
				pixelCrop.y,
				pixelCrop.width,
				pixelCrop.height,
				0,
				0,
				targetSize,
				targetSize
			);

			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error("Canvas is empty"));
						return;
					}
					const fileUrl = URL.createObjectURL(blob);
					resolve(fileUrl);
				},
				"image/jpeg",
				0.95
			);
		});
	};

	const startRecording = () => {
		if (!streamRef.current || !videoRef.current) {
			return;
		}

		recordedChunksRef.current = [];

		try {
			// Create a canvas to crop the video to phone size (9:16 aspect ratio)
			const video = videoRef.current;
			const canvas = document.createElement("canvas");

			// Set canvas to phone dimensions (480x854 for 9:16 ratio)
			const targetWidth = 480;
			const targetHeight = 854; // 480 * 16/9
			canvas.width = targetWidth;
			canvas.height = targetHeight;

			const ctx = canvas.getContext("2d");

			// Calculate crop dimensions to get center crop at 9:16
			const videoAspect = video.videoWidth / video.videoHeight;
			const targetAspect = 9 / 16;

			let sourceWidth, sourceHeight, sourceX, sourceY;

			if (videoAspect > targetAspect) {
				// Video is wider - crop sides
				sourceHeight = video.videoHeight;
				sourceWidth = sourceHeight * targetAspect;
				sourceX = (video.videoWidth - sourceWidth) / 2;
				sourceY = 0;
			} else {
				// Video is taller - crop top/bottom
				sourceWidth = video.videoWidth;
				sourceHeight = sourceWidth / targetAspect;
				sourceX = 0;
				sourceY = (video.videoHeight - sourceHeight) / 2;
			}

			// Capture frames and draw to canvas
			const fps = 30;
			const captureInterval = setInterval(() => {
				// Flip horizontally for front-facing camera (mirror view)
				if (facingMode === "user") {
					ctx.save();
					ctx.translate(targetWidth, 0);
					ctx.scale(-1, 1);
				}

				ctx.drawImage(
					video,
					sourceX,
					sourceY,
					sourceWidth,
					sourceHeight,
					0,
					0,
					targetWidth,
					targetHeight
				);

				// Reset transform for front-facing camera
				if (facingMode === "user") {
					ctx.restore();
				}
			}, 1000 / fps);

			// Get stream from canvas
			const canvasStream = canvas.captureStream(fps);

			// Add audio from original stream if available and not muted
			const audioTracks = streamRef.current?.getAudioTracks() || [];
			if (audioTracks.length > 0 && !isMuted) {
				canvasStream.addTrack(audioTracks[0]);
			}

			// Try different codecs for better compatibility
			// Prefer VP8 as it's more widely supported and plays smoother
			let options = {
				mimeType: "video/webm;codecs=vp8",
				videoBitsPerSecond: 2500000, // 2.5 Mbps for good quality/size balance
			};
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				options = {
					mimeType: "video/webm;codecs=vp9",
					videoBitsPerSecond: 2000000, // Slightly lower for VP9
				};
			}
			if (!MediaRecorder.isTypeSupported(options.mimeType)) {
				options = {
					mimeType: "video/webm",
					videoBitsPerSecond: 2500000,
				};
			}

			const mediaRecorder = new MediaRecorder(canvasStream, options);

			// Store canvas and interval for cleanup
			mediaRecorder.captureInterval = captureInterval;
			mediaRecorder.canvas = canvas;

			mediaRecorder.ondataavailable = (event) => {
				console.log("Data available:", event.data.size);
				if (event.data && event.data.size > 0) {
					recordedChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				console.log(
					"Recording stopped, chunks:",
					recordedChunksRef.current.length
				);

				// Wait longer to ensure all chunks are finalized (especially important for desktop)
				setTimeout(() => {
					if (recordedChunksRef.current.length === 0) {
						console.error("No video data recorded");
						setError("No video data was recorded. Please try again.");
						return;
					}

					// Use the mimeType from the recorder for the blob
					const mimeType = mediaRecorder.mimeType || "video/webm";
					const blob = new Blob(recordedChunksRef.current, {
						type: mimeType,
					});
					console.log("Blob created:", blob.size, "bytes", "type:", mimeType);

					// Verify blob is valid
					if (blob.size === 0) {
						console.error("Blob is empty");
						setError("Video recording is empty. Please try again.");
						return;
					}

					// Stop camera stream before creating video URL (frees resources)
					if (streamRef.current) {
						streamRef.current.getTracks().forEach((track) => track.stop());
						streamRef.current = null;
						setStream(null);
						console.log("Camera stream stopped");
					}

					// Clean up canvas and interval
					if (mediaRecorder.captureInterval) {
						clearInterval(mediaRecorder.captureInterval);
					}

					// Create blob URL and wait a bit for it to be ready (helps with desktop buffering)
					const videoUrl = URL.createObjectURL(blob);
					console.log("Video URL created:", videoUrl);

					// Give the blob URL a moment to be fully ready before setting state
					// This helps prevent buffering issues on desktop
					setTimeout(() => {
						setCapturedVideo(videoUrl);
						setIsUploadedMedia(false); // Mark as camera-captured
						console.log("capturedVideo state updated (after delay)");
					}, 200); // Additional delay for blob URL to be ready

					if (!showPreview) {
						const file = new File([blob], `video-${Date.now()}.webm`, {
							type: mimeType,
						});
						onCapture(file);
						handleClose();
					}
				}, 300); // Longer delay to ensure all chunks are fully collected and finalized
			};

			// Request data every 100ms to ensure we capture data
			mediaRecorder.start(100);
			mediaRecorderRef.current = mediaRecorder;
			setIsRecording(true);
			console.log("Recording started with mimeType:", options.mimeType);
		} catch (err) {
			console.error("Error starting recording:", err);
			setError("Unable to start recording");
		}
	};

	const stopRecording = () => {
		if (mediaRecorderRef.current && isRecording) {
			// Request final data before stopping
			if (mediaRecorderRef.current.state === "recording") {
				mediaRecorderRef.current.requestData();
			}

			// Clean up canvas capture interval
			if (mediaRecorderRef.current.captureInterval) {
				clearInterval(mediaRecorderRef.current.captureInterval);
			}

			mediaRecorderRef.current.stop();
			setIsRecording(false);
			console.log("Stop recording called");
		}
	};

	const handleConfirm = () => {
		if (capturedImage) {
			// Convert captured image to file
			fetch(capturedImage)
				.then((res) => res.blob())
				.then((blob) => {
					const file = new File([blob], `photo-${Date.now()}.jpg`, {
						type: "image/jpeg",
					});
					onCapture(file);
					handleClose();
				});
		} else if (capturedVideo) {
			// Convert captured video to file
			fetch(capturedVideo)
				.then((res) => res.blob())
				.then((blob) => {
					const file = new File([blob], `video-${Date.now()}.webm`, {
						type: "video/webm",
					});
					onCapture(file);
					handleClose();
				});
		} else if (capturedAudio) {
			// Convert captured audio to file
			fetch(capturedAudio)
				.then((res) => res.blob())
				.then((blob) => {
					const file = new File([blob], `audio-${Date.now()}.webm`, {
						type: "audio/webm",
					});
					onCapture(file);
					handleClose();
				});
		}
	};

	const startAudioRecording = async () => {
		try {
			setError(null);

			// Stop camera if running
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
				setStream(null);
			}

			// Get user media stream for audio only
			const audioStream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			audioRecorderRef.current = audioStream;

			// Add event listener to detect when microphone is disconnected
			const handleAudioTrackEnded = (event) => {
				const track = event.target;
				console.warn("Audio track ended unexpectedly:", track.label);

				if (isAudioRecording) {
					// Stop recording if active
					stopAudioRecording();
					setError(
						"Microphone connection lost during recording. Please check your microphone connection and try again."
					);
				} else {
					setError(
						"Microphone connection lost. Please check your microphone connection and try again."
					);
				}
				setHasPermission(false);

				// Clean up
				if (audioRecorderRef.current) {
					audioRecorderRef.current.getTracks().forEach((t) => t.stop());
					audioRecorderRef.current = null;
				}
			};

			// Add ended listener to audio track
			audioStream.getAudioTracks().forEach((track) => {
				track.addEventListener("ended", handleAudioTrackEnded);
			});

			// Set up Web Audio API for visualization
			audioContextRef.current = new (window.AudioContext ||
				window.webkitAudioContext)();
			analyserRef.current = audioContextRef.current.createAnalyser();
			const source =
				audioContextRef.current.createMediaStreamSource(audioStream);
			source.connect(analyserRef.current);
			analyserRef.current.fftSize = 2048; // Higher FFT size for smoother waveform
			analyserRef.current.smoothingTimeConstant = 0.8; // Smooth out the waveform

			// Create media recorder
			mediaRecorderRef.current = new MediaRecorder(audioStream);
			recordedChunksRef.current = [];

			// When data available, add to chunks
			mediaRecorderRef.current.ondataavailable = (e) => {
				if (e.data.size > 0) {
					recordedChunksRef.current.push(e.data);
				}
			};

			// When recording stops, create a blob and set media state
			mediaRecorderRef.current.onstop = () => {
				const audioBlob = new Blob(recordedChunksRef.current, {
					type: "audio/webm",
				});

				// Create object URL for audio preview
				const audioUrl = URL.createObjectURL(audioBlob);
				setCapturedAudio(audioUrl);
				setIsUploadedMedia(false); // Mark as camera-captured

				// Stop all audio tracks
				if (audioRecorderRef.current) {
					audioRecorderRef.current.getTracks().forEach((track) => track.stop());
				}

				// Clean up audio context
				if (audioContextRef.current) {
					audioContextRef.current.close();
					audioContextRef.current = null;
				}
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current);
					animationFrameRef.current = null;
				}
				setAudioLevels([]);

				if (!showPreview) {
					const file = new File([audioBlob], `audio-${Date.now()}.webm`, {
						type: "audio/webm",
					});
					onCapture(file);
					handleClose();
				}
			};

			// Start recording
			mediaRecorderRef.current.start();
			setIsAudioRecording(true);
			setRecordingDuration(0);

			// Start visualization loop using time-domain data for oscilloscope effect
			const bufferLength = analyserRef.current.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			const updateVisualization = () => {
				if (
					!analyserRef.current ||
					!mediaRecorderRef.current ||
					mediaRecorderRef.current.state !== "recording"
				) {
					if (animationFrameRef.current) {
						cancelAnimationFrame(animationFrameRef.current);
						animationFrameRef.current = null;
					}
					return;
				}
				// Use time-domain data for oscilloscope-style waveform
				analyserRef.current.getByteTimeDomainData(dataArray);
				// Use all data points for smooth waveform (or downsample if needed)
				const waveformData = Array.from(dataArray);
				setAudioLevels(waveformData);
				animationFrameRef.current = requestAnimationFrame(updateVisualization);
			};
			updateVisualization();
		} catch (error) {
			console.error("Error accessing microphone:", error);
			setError(
				"Could not access microphone. Please check your browser permissions."
			);
			setIsAudioRecording(false);
		}
	};

	const stopAudioRecording = () => {
		if (mediaRecorderRef.current && isAudioRecording) {
			mediaRecorderRef.current.stop();

			// Clean up audio context and animation
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setAudioLevels([]);

			setIsAudioRecording(false);
		}
	};

	const handleRetake = async () => {
		// Clean up crop state
		if (imageToCrop) {
			URL.revokeObjectURL(imageToCrop);
			setImageToCrop(null);
		}
		setShowCrop(false);
		setCrop(null);
		setCompletedCrop(null);

		// Clean up blob URLs from previous captures to free memory
		if (capturedImage) {
			URL.revokeObjectURL(capturedImage);
		}
		if (capturedVideo) {
			URL.revokeObjectURL(capturedVideo);
		}
		if (capturedAudio) {
			URL.revokeObjectURL(capturedAudio);
		}

		// Stop audio recording if active
		if (isAudioRecording) {
			stopAudioRecording();
		}
		if (audioRecorderRef.current) {
			audioRecorderRef.current.getTracks().forEach((track) => track.stop());
			audioRecorderRef.current = null;
		}
		if (audioContextRef.current) {
			audioContextRef.current.close();
			audioContextRef.current = null;
		}
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		playbackAnalyserRef.current = null;

		// Clear captured media
		setCapturedImage(null);
		setCapturedVideo(null);
		setCapturedAudio(null);
		setAudioLevels([]);
		setRecordingDuration(0);
		setError(null);

		// Clear video element source before restarting
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}
		// Clear audio element
		if (audioElementRef.current) {
			audioElementRef.current.pause();
			audioElementRef.current.src = "";
		}

		// If media was uploaded, trigger file picker again instead of starting camera
		if (isUploadedMedia) {
			// Reset file input values so onChange will fire even if same file is selected
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			if (videoInputRef.current) {
				videoInputRef.current.value = "";
			}
			if (audioInputRef.current) {
				audioInputRef.current.value = "";
			}

			setIsUploadedMedia(false);

			// Small delay to ensure state is cleared before opening file picker
			setTimeout(() => {
				// Trigger appropriate file input based on what was uploaded
				if (mode === "photo") {
					fileInputRef.current?.click();
				} else if (mode === "video") {
					videoInputRef.current?.click();
				} else if (mode === "audio") {
					audioInputRef.current?.click();
				}
			}, 100);
		} else if (mode !== "audio") {
			// Restart camera (startCamera will handle stopping any existing stream)
			// Skip for audio mode - microphone will be requested when user clicks record
			await startCamera();
		}
	};

	const handleFileSelect = (e, type) => {
		const file = e.target.files[0];
		console.log("handleFileSelect called - type:", type, "file:", file);

		if (!file) {
			console.log("No file selected");
			return;
		}

		setError(null);

		// Validate file type
		const validTypes = {
			image: ["image/jpeg", "image/png", "image/gif", "image/webp"],
			video: [
				"video/mp4",
				"video/webm",
				"video/ogg",
				"video/quicktime",
				"video/x-m4v",
			],
			audio: [
				"audio/mpeg",
				"audio/wav",
				"audio/ogg",
				"audio/webm",
				"audio/mp4",
			],
		};

		if (!validTypes[type]?.includes(file.type)) {
			console.error(`Invalid file type: ${file.type} for type: ${type}`);
			setError(`Invalid file type. Please select a valid ${type} file.`);
			return;
		}

		// Check file size (100MB max for videos, 50MB for images/audio)
		const maxSize = type === "video" ? 100 * 1024 * 1024 : 50 * 1024 * 1024; // 100MB for video, 50MB for images/audio
		if (file.size > maxSize) {
			const maxSizeMB = type === "video" ? "100MB" : "50MB";
			setError(`File too large. Maximum size is ${maxSizeMB}.`);
			return;
		}

		console.log("File validation passed, creating preview URL");
		// Create preview URL
		const previewUrl = URL.createObjectURL(file);
		console.log("Preview URL created:", previewUrl);

		if (type === "image") {
			// For images, show crop interface (same as camera capture)
			console.log("Setting image to crop");
			setImageToCrop(previewUrl);
			setShowCrop(true);
			setIsUploadedMedia(true); // Mark as uploaded
		} else if (type === "video") {
			console.log("Setting capturedVideo for uploaded video:", previewUrl);
			setCapturedVideo(previewUrl);
			setIsUploadedMedia(true); // Mark as uploaded
			// Stop camera if running
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
				setStream(null);
			}
		} else if (type === "audio") {
			console.log("Setting captured audio");
			setCapturedAudio(previewUrl);
			setIsUploadedMedia(true); // Mark as uploaded
		}

		// If showPreview is false, immediately call onCapture
		if (!showPreview) {
			onCapture(file);
			handleClose();
		}
	};

	const handleClose = () => {
		stopCamera();
		onClose();
	};

	const formatDuration = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Set up audio visualization for playback - only create Web Audio API once
	useEffect(() => {
		if (!capturedAudio || !audioElementRef.current) {
			return;
		}

		const audioElement = audioElementRef.current;

		// Create audio context and analyser for playback (only once)
		try {
			if (!audioContextRef.current) {
				audioContextRef.current = new (window.AudioContext ||
					window.webkitAudioContext)();
			}

			if (!playbackAnalyserRef.current) {
				playbackAnalyserRef.current = audioContextRef.current.createAnalyser();
				playbackAnalyserRef.current.fftSize = 2048;
				playbackAnalyserRef.current.smoothingTimeConstant = 0.8; // Smooth out the waveform

				const source =
					audioContextRef.current.createMediaElementSource(audioElement);
				source.connect(playbackAnalyserRef.current);
				playbackAnalyserRef.current.connect(
					audioContextRef.current.destination
				);
			}
		} catch (error) {
			console.error("Error setting up audio visualization:", error);
		}

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
		};
	}, [capturedAudio]);

	// Ensure preview video plays when captured
	useEffect(() => {
		if (!capturedVideo || !previewVideoRef.current) {
			return;
		}

		const video = previewVideoRef.current;

		// Clear any existing srcObject to ensure clean state
		video.srcObject = null;

		// Ensure video is muted for autoplay (required by browsers)
		video.muted = true;

		// Wait for blob URL to be ready, then set src
		const initVideo = () => {
			if (!video.src || video.src !== capturedVideo) {
				video.src = capturedVideo;
				console.log("[Desktop] Video src set:", capturedVideo);
			}

			// Explicitly load the video (required for desktop browsers with blob URLs)
			if (video.readyState === 0) {
				video.load();
				console.log("[Desktop] Video load() called");
			}
		};

		// Longer delay to ensure blob URL is fully ready (helps with desktop buffering)
		const initTimeout = setTimeout(initVideo, 300);

		let hasStartedPlaying = false;

		// Function to attempt playing the video - only play when fully buffered
		const tryPlay = async () => {
			// Prevent multiple play attempts
			if (hasStartedPlaying) {
				return;
			}

			try {
				// Only play when we have enough data buffered (readyState >= 4 = HAVE_ENOUGH_DATA)
				// This prevents the "choking up" / buffering loop issue on desktop
				if (video.readyState >= 4 && video.paused) {
					video.currentTime = 0; // Reset to start
					video.muted = true;
					await video.play();
					hasStartedPlaying = true;
					console.log(
						"[Desktop] ✅ Video started playing successfully (fully buffered)"
					);
				} else if (video.readyState >= 4 && !video.paused) {
					// Video is fully buffered and already playing
					hasStartedPlaying = true;
				} else {
					console.log(
						"[Desktop] ⏳ Waiting for more buffering - readyState:",
						video.readyState,
						"(need >= 4 for smooth playback)"
					);
				}
			} catch (error) {
				console.error("[Desktop] ❌ Error playing video:", error);
				// Controls are visible, user can play manually
			}
		};

		// Handle when video can play through (fully buffered) - this is the key event for desktop
		const handleCanPlayThrough = () => {
			console.log(
				"[Desktop] 🎬 Can play through (fully buffered) - readyState:",
				video.readyState
			);
			video.muted = true;
			// Only try to play when fully buffered to prevent buffering loops
			if (!hasStartedPlaying) {
				tryPlay();
			}
		};

		// Handle when video can play (partially buffered) - just log, don't play yet
		const handleCanPlay = () => {
			console.log(
				"[Desktop] Can play (partially buffered) - readyState:",
				video.readyState
			);
			video.muted = true;
			// Don't play here - wait for canplaythrough for smoother playback
		};

		const handleLoadedMetadata = () => {
			console.log(
				"[Desktop] Metadata loaded - duration:",
				video.duration,
				"readyState:",
				video.readyState
			);
			video.muted = true;
		};

		const handleLoadedData = () => {
			console.log("[Desktop] 📦 Data loaded - readyState:", video.readyState);
			video.muted = true;
			// Don't try to play here - wait for more buffering
		};

		const handleProgress = () => {
			// Log buffering progress
			if (video.buffered.length > 0) {
				const bufferedEnd = video.buffered.end(video.buffered.length - 1);
				const bufferedPercent = (bufferedEnd / video.duration) * 100;
				console.log(
					"[Desktop] Buffering progress:",
					`${bufferedPercent.toFixed(1)}%`,
					"readyState:",
					video.readyState
				);

				// If we have enough buffered and haven't started playing, try to play
				if (
					bufferedPercent >= 50 &&
					video.readyState >= 4 &&
					!hasStartedPlaying
				) {
					tryPlay();
				}
			}
		};

		const handleWaiting = () => {
			console.log("[Desktop] ⏳ Waiting for data (buffering)...");
		};

		const handleStalled = () => {
			console.log("[Desktop] ⚠️ Video stalled (buffering issue)");
		};

		// Add event listeners - wait for canplaythrough before playing
		video.addEventListener("canplaythrough", handleCanPlayThrough, {
			once: true,
		});
		video.addEventListener("canplay", handleCanPlay, { once: false });
		video.addEventListener("loadedmetadata", handleLoadedMetadata, {
			once: false,
		});
		video.addEventListener("loadeddata", handleLoadedData, { once: false });
		video.addEventListener("progress", handleProgress, { once: false });
		video.addEventListener("waiting", handleWaiting, { once: false });
		video.addEventListener("stalled", handleStalled, { once: false });

		// Fallback: after a longer delay, check if video is ready and hasn't started
		const timeoutId = setTimeout(() => {
			console.log(
				"[Desktop] Final check - src:",
				video.src,
				"readyState:",
				video.readyState,
				"paused:",
				video.paused,
				"hasStartedPlaying:",
				hasStartedPlaying
			);

			// Only try to play if we have enough data and haven't started yet
			if (
				video.src &&
				video.readyState >= 4 &&
				video.paused &&
				!hasStartedPlaying
			) {
				console.log(
					"[Desktop] ✅ Final check: Video fully buffered, attempting play"
				);
				tryPlay();
			} else if (video.src && video.readyState === 0) {
				// Video has src but hasn't started loading - trigger load again
				console.log("[Desktop] Video src set but not loading, triggering load");
				video.load();
			}
		}, 1000); // Longer delay to ensure blob is fully ready and buffered

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(initTimeout);
			hasStartedPlaying = false;
			video.removeEventListener("canplaythrough", handleCanPlayThrough);
			video.removeEventListener("canplay", handleCanPlay);
			video.removeEventListener("loadedmetadata", handleLoadedMetadata);
			video.removeEventListener("loadeddata", handleLoadedData);
			video.removeEventListener("progress", handleProgress);
			video.removeEventListener("waiting", handleWaiting);
			video.removeEventListener("stalled", handleStalled);
		};
	}, [capturedVideo]);

	// Render camera using portal to ensure it's above all Dialog components (z-50)
	const cameraContent = (
		<>
			{/* Backdrop to block all events from underlying modals */}
			<div
				className="fixed inset-0 z-[99998] bg-black/50"
				style={{ pointerEvents: "auto" }}
				onClick={(e) => e.stopPropagation()}
				onTouchStart={(e) => e.stopPropagation()}
				onTouchEnd={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			/>
			<div
				className="fixed inset-0 z-[99999] flex flex-col bg-black"
				style={{ pointerEvents: "auto" }}
				onClick={(e) => {
					e.stopPropagation();
					handleUserInteraction();
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
					handleUserInteraction();
				}}
				onTouchEnd={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div className="absolute left-0 right-0 top-0 z-10 flex items-start justify-between bg-gradient-to-b from-black/80 to-transparent p-4">
					{/* Left side - Post type indicator and mute button */}
					<div className="flex flex-col gap-2">
						{/* Post type indicator - top left */}
						{postType || isContest ? (
							<div>
								{isContest ? (
									<div className="flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-lg">
										<Star size={16} />
										<span>Contest Submission</span>
									</div>
								) : null}
								{!isContest && postType === "glow" && (
									<div className="flex items-center gap-2 rounded-full bg-yellow-500 px-4 py-2 text-sm font-semibold text-white shadow-lg">
										<Star size={16} />
										<span>Glow Post</span>
									</div>
								)}
								{!isContest && postType === "pride" && (
									<div className="flex items-center gap-2 rounded-full bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-lg">
										<Award size={16} />
										<span>Proud Post</span>
									</div>
								)}
								{!isContest && postType === "grateful" && (
									<div className="flex items-center gap-2 rounded-full bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-lg">
										<Heart size={16} />
										<span>Grateful Post</span>
									</div>
								)}
							</div>
						) : null}

						{/* Mute button (only show in video mode when recording or previewing) */}
						{mode === "video" && (isRecording || capturedVideo) ? (
							<Button
								onClick={() => {
									const newMutedState = !isMuted;
									setIsMuted(newMutedState);
									// Mute/unmute audio tracks in the stream
									if (streamRef.current) {
										const audioTracks = streamRef.current.getAudioTracks();
										audioTracks.forEach((track) => {
											track.enabled = !newMutedState; // enabled = true when not muted
										});
									}
								}}
								variant="ghost"
								size="icon"
								className="text-white hover:bg-white/20"
							>
								{isMuted ? (
									<VolumeX size={24} className="text-red-500" />
								) : (
									<Volume2 size={24} className="text-green-500" />
								)}
							</Button>
						) : null}
					</div>

					{/* Right side - Close and flip camera buttons */}
					<div className="flex flex-col gap-2">
						{/* X close button - red background */}
						<Button
							onClick={handleClose}
							variant="ghost"
							size="icon"
							className="bg-red-500 text-white hover:bg-red-600"
						>
							<X size={24} />
						</Button>

						{/* Flip camera button - only show on mobile/tablet devices with multiple cameras when camera is active (not in preview modes) */}
						{hasMultipleCameras &&
						!capturedImage &&
						!capturedVideo &&
						!capturedAudio &&
						mode !== "audio" ? (
							<Button
								onClick={() => {
									// Toggle between front and back camera
									const newFacingMode =
										facingMode === "user" ? "environment" : "user";
									setFacingMode(newFacingMode);
									// Camera will restart automatically due to useEffect dependency on facingMode
								}}
								variant="ghost"
								size="icon"
								className="text-white hover:bg-white/20 md:hidden"
								title="Flip camera"
							>
								<RotateCw size={24} />
							</Button>
						) : null}
					</div>
				</div>

				{/* Hidden file inputs */}
				<input
					ref={fileInputRef}
					type="file"
					accept={
						autoTriggerUpload
							? "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg,video/quicktime"
							: "image/jpeg,image/png,image/gif,image/webp"
					}
					onChange={(e) => {
						const file = e.target.files[0];
						if (file) {
							// Detect type from file
							if (file.type.startsWith("video/")) {
								handleFileSelect(e, "video");
							} else {
								handleFileSelect(e, "image");
							}
						}
					}}
					className="hidden"
				/>
				<input
					ref={videoInputRef}
					type="file"
					accept="video/mp4,video/webm,video/ogg,video/quicktime"
					onChange={(e) => handleFileSelect(e, "video")}
					className="hidden"
				/>
				<input
					ref={audioInputRef}
					type="file"
					accept="audio/mpeg,audio/wav,audio/ogg,audio/webm,audio/mp4"
					onChange={(e) => handleFileSelect(e, "audio")}
					className="hidden"
				/>

				{/* Preview controls - moved to top to avoid overlapping video player controls */}
				{capturedImage || capturedVideo || capturedAudio ? (
					<div className="absolute left-0 right-0 top-16 z-10 flex items-center justify-center gap-2 p-2 sm:gap-4 sm:p-4">
						<div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
							{/* Undo button - only show for images (to go back to crop screen) */}
							{capturedImage && imageToCrop ? (
								<Button
									onClick={handleUndo}
									size="lg"
									variant="outline"
									className="border-white/30 bg-white/10 px-4 text-sm text-white backdrop-blur-sm hover:bg-white/20 sm:px-8 sm:text-base"
									title="Undo crop and return to crop screen"
								>
									<Undo2 size={20} className="mr-1 sm:mr-2" />
									Undo
								</Button>
							) : null}
							<Button
								onClick={handleRetake}
								size="lg"
								variant="outline"
								className="border-white/30 bg-white/10 px-4 text-sm text-white backdrop-blur-sm hover:bg-white/20 sm:px-8 sm:text-base"
							>
								{isUploadedMedia ? "Choose Different" : "Retake"}
							</Button>
							<Button
								onClick={handleConfirm}
								size="lg"
								className="bg-green-500 px-4 text-sm text-white hover:bg-green-600 sm:px-8 sm:text-base"
							>
								<CheckCircle size={20} className="mr-1 sm:mr-2" />
								Use{" "}
								{mode === "photo"
									? "Photo"
									: mode === "video"
										? "Video"
										: mode === "audio"
											? "Audio"
											: "Media"}
							</Button>
						</div>
					</div>
				) : null}

				{/* Camera View or Preview */}
				<div className="relative flex flex-1 items-center justify-center bg-black">
					{isInitializing && !error ? (
						<div className="flex flex-col items-center justify-center p-8 text-center text-white">
							<CameraIcon
								size={64}
								className="mx-auto mb-4 animate-pulse opacity-50"
							/>
							<p className="text-sm text-gray-400">Initializing camera...</p>
						</div>
					) : error ? (
						<div className="flex flex-col items-center justify-center p-4 pb-32 text-center text-white sm:p-8 sm:pb-24">
							<CameraIcon size={64} className="mx-auto mb-4 opacity-50" />
							<p className="mb-4 text-base font-semibold sm:text-lg">{error}</p>
							{error.includes("denied") || error.includes("Permission") ? (
								<div className="flex flex-col items-center space-y-3">
									<p className="text-xs text-gray-300 sm:text-sm">
										To enable camera access:
									</p>
									<ul className="mx-auto max-w-md space-y-2 text-center text-xs text-gray-400 sm:text-sm">
										<li className="flex items-start justify-center gap-2">
											<span className="mt-1">1.</span>
											<span>
												Look for the camera icon in your browser's address bar
											</span>
										</li>
										<li className="flex items-start justify-center gap-2">
											<span className="mt-1">2.</span>
											<span>Click it and select "Allow" for camera access</span>
										</li>
										<li className="flex items-start justify-center gap-2">
											<span className="mt-1">3.</span>
											<span>Refresh this page and try again</span>
										</li>
									</ul>
									<Button
										onClick={() => {
											setError(null);
											startCamera();
										}}
										className="mt-4 bg-white text-black hover:bg-gray-200"
									>
										Try Again
									</Button>
								</div>
							) : error.includes("connection lost") ||
							  error.includes("Connection lost") ? (
								<div className="flex flex-col items-center space-y-3">
									<p className="text-xs text-gray-300 sm:text-sm">
										{error.includes("Camera")
											? "Please reconnect your camera and try again."
											: "Please reconnect your microphone and try again."}
									</p>
									<Button
										onClick={() => {
											setError(null);
											if (mode === "audio") {
												// For audio mode, just clear error - user will need to click record again
												setHasPermission(true);
											} else {
												startCamera();
											}
										}}
										className="mt-4 bg-white text-black hover:bg-gray-200"
									>
										Try Again
									</Button>
								</div>
							) : (
								<div className="flex flex-col items-center space-y-3">
									<p className="text-xs text-gray-400 sm:text-sm">
										Please check your camera connection and browser settings
									</p>
									<Button
										onClick={() => {
											setError(null);
											if (mode !== "audio") {
												startCamera();
											}
										}}
										className="mt-4 bg-white text-black hover:bg-gray-200"
									>
										Try Again
									</Button>
								</div>
							)}
						</div>
					) : showCrop && imageToCrop ? (
						<div className="relative flex h-full w-full items-center justify-center bg-black p-4">
							<div style={{ maxWidth: "90vw", maxHeight: "70vh" }}>
								<ReactCrop
									crop={crop}
									onChange={onCropChange}
									onComplete={onCropComplete}
									aspect={1}
									minWidth={100}
									minHeight={100}
									keepSelection
									disabled={false}
								>
									<img
										ref={imgRef}
										src={imageToCrop}
										alt="Crop"
										style={{
											maxWidth: "90vw",
											maxHeight: "70vh",
											width: "auto",
											height: "auto",
											objectFit: "contain",
											display: "block",
										}}
										onLoad={onImageLoaded}
									/>
								</ReactCrop>
							</div>
						</div>
					) : capturedImage ? (
						<img
							src={capturedImage}
							alt="Captured"
							style={{
								maxWidth: "90vw",
								maxHeight: "70vh",
								width: "auto",
								height: "auto",
								objectFit: "contain",
							}}
						/>
					) : capturedVideo ? (
						<div className="relative flex h-full w-full items-center justify-center bg-black">
							<video
								key={capturedVideo} // Force re-render when video changes
								ref={previewVideoRef}
								src={capturedVideo}
								controls
								autoPlay
								muted
								playsInline
								loop
								preload="auto"
								className="max-h-full max-w-full rounded-lg border-2 border-white/20 bg-black object-contain shadow-2xl"
								style={{
									width: "100%",
									height: "100%",
									outline: "none",
									maxWidth: "480px",
									aspectRatio: "9 / 16",
								}}
								onLoadedMetadata={(e) => {
									const video = e.target;
									console.log(
										"[Video] Metadata loaded - duration:",
										video.duration,
										"readyState:",
										video.readyState,
										"src:",
										video.src
									);
									// Ensure muted for autoplay
									video.muted = true;
								}}
								onLoadedData={(e) => {
									const video = e.target;
									console.log(
										"[Video] Data loaded - readyState:",
										video.readyState
									);
									// Ensure muted for autoplay
									video.muted = true;
								}}
								onCanPlay={(e) => {
									const video = e.target;
									console.log(
										"[Video] Can play (partial buffering) - paused:",
										video.paused,
										"readyState:",
										video.readyState
									);
									// Ensure muted - wait for canplaythrough for full buffering before playing
									video.muted = true;
									// Don't play here - wait for full buffering to prevent buffering loops
								}}
								onCanPlayThrough={(e) => {
									const video = e.target;
									console.log(
										"[Video] Can play through (fully buffered) - readyState:",
										video.readyState
									);
									// Video is fully buffered - ensure muted for autoplay
									video.muted = true;
									// Let useEffect handle playback to avoid conflicts
								}}
								onPlay={() => {
									console.log("[Video] Started playing successfully!");
								}}
								onPlaying={() => {
									console.log("[Video] Video is now playing");
								}}
								onError={(e) => {
									const video = e.target;
									console.error(
										"[Video] Error - code:",
										video.error?.code,
										"message:",
										video.error?.message,
										"src:",
										video.src
									);
									setError(
										"Error loading video preview. Please try recording again."
									);
								}}
								onWaiting={() => {
									console.log("[Video] Waiting for data...");
								}}
							>
								Your browser does not support the video tag.
							</video>
							{/* Instruction overlay */}
							<div className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 transform rounded-full border border-white/20 bg-black/80 px-4 py-2 text-sm text-white backdrop-blur-sm">
								Tap video to pause/play
							</div>
							{/* Visual indicator that this is a video player */}
							<div className="pointer-events-none absolute bottom-20 left-1/2 -translate-x-1/2 transform rounded-md border border-white/20 bg-black/60 px-3 py-1 text-xs text-white backdrop-blur-sm">
								Video Preview (Looping)
							</div>
						</div>
					) : capturedAudio ? (
						<div className="relative flex h-full w-full flex-col items-center justify-center gap-8 bg-gradient-to-b from-black via-gray-900 to-black p-8">
							<p className="text-xl font-bold text-white">Audio Preview</p>

							{/* Combined Waveform and Player Card */}
							<div className="w-full max-w-md">
								{/* Hidden audio element */}
								<audio
									ref={audioElementRef}
									src={capturedAudio}
									onLoadedMetadata={(e) => {
										setAudioDuration(e.target.duration);
									}}
									onTimeUpdate={(e) => {
										setAudioCurrentTime(e.target.currentTime);
									}}
									onEnded={() => {
										setIsAudioPlaying(false);
										setAudioCurrentTime(0);
										// Reset waveform visualization
										if (animationFrameRef.current) {
											cancelAnimationFrame(animationFrameRef.current);
											animationFrameRef.current = null;
										}
										setAudioLevels([]);
									}}
									className="hidden"
								/>

								{/* Single card containing both waveform and controls */}
								<div
									className="w-full space-y-4 rounded-2xl border-2 p-4"
									style={{
										backgroundColor: "rgba(0, 0, 0, 0.9)",
										borderColor: "rgba(234, 179, 8, 0.3)",
									}}
								>
									{/* Waveform visualization */}
									<Waveform audioLevels={audioLevels} isRecording={false} />

									{/* Simplified Audio Controls - Progress bar */}
									<div className="space-y-3">
										<div className="flex items-center gap-4">
											{/* Play/Pause Button */}
											<button
												onClick={() => {
													if (audioElementRef.current) {
														if (isAudioPlaying) {
															audioElementRef.current.pause();
															setIsAudioPlaying(false);
														} else {
															audioElementRef.current.play();
															setIsAudioPlaying(true);

															// Restart visualization if it was stopped
															if (
																!animationFrameRef.current &&
																playbackAnalyserRef.current
															) {
																const bufferLength =
																	playbackAnalyserRef.current.frequencyBinCount;
																const dataArray = new Uint8Array(bufferLength);

																const visualize = () => {
																	if (!playbackAnalyserRef.current) {
																		return;
																	}

																	playbackAnalyserRef.current.getByteTimeDomainData(
																		dataArray
																	);
																	const waveformData = Array.from(dataArray);
																	setAudioLevels(waveformData);

																	animationFrameRef.current =
																		requestAnimationFrame(visualize);
																};

																visualize();
															}
														}
													}
												}}
												className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-lg transition-all"
												style={{
													borderColor: "#fbbf24",
													backgroundColor: "#fbbf24",
													boxShadow: isAudioPlaying
														? "0 0 20px rgba(251, 191, 36, 0.6)"
														: "0 0 10px rgba(251, 191, 36, 0.3)",
												}}
												aria-label={
													isAudioPlaying ? "Pause audio" : "Play audio"
												}
											>
												{isAudioPlaying ? (
													<Pause size={20} fill="white" color="white" />
												) : (
													<Play size={20} fill="white" color="white" />
												)}
											</button>

											{/* Progress Bar */}
											<div className="flex-1">
												<div className="relative h-2 overflow-hidden rounded-full bg-gray-700">
													<div
														className="absolute left-0 top-0 h-full rounded-full transition-all duration-200"
														style={{
															width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
															backgroundColor: "#fbbf24",
															boxShadow: "0 0 10px rgba(251, 191, 36, 0.6)",
														}}
													/>
												</div>
											</div>
										</div>

										{/* Duration Display */}
										<div
											className="flex w-full justify-between px-2 font-mono text-xs font-semibold"
											style={{ color: "#fbbf24" }}
										>
											<div>{formatDuration(Math.floor(audioCurrentTime))}</div>
											<div>{formatDuration(Math.floor(audioDuration))}</div>
										</div>
									</div>
								</div>
							</div>

							<p className="max-w-sm text-center text-sm text-gray-400">
								Press play to listen to your audio
							</p>
						</div>
					) : autoTriggerUpload &&
					  !imageToCrop &&
					  !capturedVideo &&
					  !capturedAudio ? (
						// Upload placeholder - show placeholder for where media will appear
						<div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-b from-gray-900 via-black to-gray-900 p-8">
							<div className="relative">
								{/* Dashed border placeholder */}
								<div className="flex h-64 w-64 items-center justify-center rounded-2xl border-4 border-dashed border-gray-600 bg-gray-800/50 backdrop-blur-sm">
									<div className="text-center">
										<img
											src={`${IMAGE_PATH}/newsfeed/upload.png`}
											alt="Upload"
											className="mx-auto mb-4 h-16 w-auto opacity-60"
										/>
										<p className="text-lg font-semibold text-gray-300">
											{mode === "photo" ? "Image Preview" : "Video Preview"}
										</p>
										<p className="mt-2 text-sm text-gray-500">
											Your {mode === "photo" ? "image" : "video"} will appear
											here
										</p>
									</div>
								</div>
							</div>

							<div className="text-center">
								<p className="text-xl font-bold text-white">
									Select a file to upload
								</p>
								<p className="mt-2 text-sm text-gray-400">
									Click the upload button below to choose a{" "}
									{mode === "photo" ? "photo" : "video"}
								</p>
							</div>
						</div>
					) : mode === "audio" ? (
						<>
							{/* Audio mode - show waveform visualization */}
							<div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-b from-black via-gray-900 to-black p-8 pb-32 sm:gap-8 sm:pb-8">
								{/* Recording status text */}
								<div className="text-center">
									<p className="text-2xl font-bold text-white">
										{isAudioRecording ? "Recording..." : "Ready to Record"}
									</p>
									{isAudioRecording ? (
										<p className="mt-2 font-mono text-xl text-yellow-400">
											{formatDuration(recordingDuration)}
										</p>
									) : null}
								</div>

								{/* Waveform visualization */}
								<div className="w-full max-w-md">
									<Waveform
										audioLevels={audioLevels}
										isRecording={isAudioRecording}
									/>
								</div>

								{/* Instructions */}
								<p className="max-w-sm text-center text-sm text-gray-400">
									{isAudioRecording
										? "Tap the button below to stop recording"
										: "Tap the microphone button below to start recording"}
								</p>
							</div>
						</>
					) : (
						<>
							<div className="flex h-full w-full items-center justify-center bg-black">
								<video
									key={`${facingMode}-${mode}`}
									ref={videoRef}
									autoPlay
									playsInline
									muted
									className="object-cover"
									style={{
										width: "100%",
										height: "100%",
										maxWidth: "480px",
										aspectRatio: "9 / 16",
										// Only mirror for front-facing camera (user), not for back camera (environment)
										transform: facingMode === "user" ? "scaleX(-1)" : "none",
										display: "block", // Ensure video is displayed
										visibility: "visible", // Ensure video is visible
									}}
									onLoadedMetadata={(e) => {
										const video = e.target;
										console.log("Camera video metadata loaded:", {
											videoWidth: video.videoWidth,
											videoHeight: video.videoHeight,
											readyState: video.readyState,
											paused: video.paused,
										});
										// Ensure video plays
										if (video.paused) {
											video.play().catch((err) => {
												console.warn(
													"Error playing video on metadata load:",
													err
												);
											});
										}
									}}
									onCanPlay={(e) => {
										const video = e.target;
										console.log("Camera video can play:", {
											paused: video.paused,
											readyState: video.readyState,
										});
										// Ensure video plays
										if (video.paused) {
											video.play().catch((err) => {
												console.warn("Error playing video on canPlay:", err);
											});
										}
									}}
									onPlay={() => {
										console.log("Camera video is now playing");
									}}
								/>
							</div>

							{/* Recording indicator */}
							{isRecording ? (
								<div className="absolute left-1/2 top-20 flex -translate-x-1/2 transform items-center gap-2 rounded-full bg-red-500 px-4 py-2 text-white shadow-lg">
									<span className="h-3 w-3 animate-pulse rounded-full bg-white" />
									<span className="font-mono text-lg font-bold">
										{formatDuration(recordingDuration)}
									</span>
								</div>
							) : null}
						</>
					)}
				</div>

				{/* Hidden canvas for photo capture */}
				<canvas ref={canvasRef} className="hidden" />

				{/* Controls */}
				<div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent">
					{autoTriggerUpload &&
					!imageToCrop &&
					!capturedVideo &&
					!capturedAudio ? (
						// Upload mode - show just the upload button
						<div className="flex flex-col items-center gap-4 pb-0">
							<div className="flex items-center justify-center pb-4">
								<Button
									onClick={() => {
										// Always use fileInputRef for upload mode (accepts both images and videos)
										fileInputRef.current?.click();
									}}
									size="lg"
									className="bg-gradient-to-r from-green-500 to-emerald-500 px-8 py-6 text-lg font-bold text-white shadow-lg hover:from-green-600 hover:to-emerald-600"
								>
									<img
										src={`${IMAGE_PATH}/newsfeed/upload.png`}
										alt="Upload"
										className="mr-3 h-6 w-auto brightness-0 invert"
									/>
									Choose File
								</Button>
							</div>
						</div>
					) : showCrop && imageToCrop ? (
						// Crop controls
						<div className="flex flex-col items-center gap-4 p-8">
							<div className="flex gap-4">
								<Button
									onClick={() => {
										setShowCrop(false);
										setCrop(null);
										if (imageToCrop) {
											URL.revokeObjectURL(imageToCrop);
											setImageToCrop(null);
										}
										// Restart camera with the same facingMode (preserved in state/localStorage)
										startCamera();
									}}
									variant="outline"
									className="border-white/30 bg-white/10 text-white hover:bg-white/20"
								>
									<X size={18} className="mr-2" />
									Cancel
								</Button>
								<Button
									onClick={applyCrop}
									disabled={!crop}
									className="bg-white text-black hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
								>
									Apply Crop
								</Button>
							</div>
							<p className="text-center text-xs text-white">
								Drag the crop box to move it, drag corners to resize
							</p>
						</div>
					) : !capturedImage && !capturedVideo && !capturedAudio ? (
						// Capture controls with mode selector
						<div className="flex flex-col items-center gap-4 pb-0">
							{/* Capture Button */}
							<div className="flex items-center justify-center pb-4">
								{mode === "photo" ? (
									<Button
										onClick={capturePhoto}
										disabled={!hasPermission || isAudioRecording}
										size="icon"
										className="h-16 w-16 rounded-full border-4 border-white bg-white text-black shadow-xl hover:bg-gray-200 sm:h-20 sm:w-20"
									>
										<Circle size={32} className="fill-current sm:hidden" />
										<Circle
											size={40}
											className="hidden fill-current sm:block"
										/>
									</Button>
								) : mode === "audio" ? (
									<Button
										onClick={
											isAudioRecording
												? stopAudioRecording
												: startAudioRecording
										}
										disabled={!hasPermission}
										size="icon"
										className={`h-16 w-16 rounded-full border-4 border-white shadow-xl transition-all sm:h-20 sm:w-20 ${
											isAudioRecording
												? "bg-red-500 hover:bg-red-600"
												: "bg-white hover:bg-gray-200"
										}`}
									>
										{isAudioRecording ? (
											<>
												<div className="h-6 w-6 rounded-sm bg-white sm:hidden" />
												<div className="hidden h-8 w-8 rounded-sm bg-white sm:block" />
											</>
										) : (
											<>
												<Mic size={32} className="text-black sm:hidden" />
												<Mic size={40} className="hidden text-black sm:block" />
											</>
										)}
									</Button>
								) : (
									<Button
										onClick={isRecording ? stopRecording : startRecording}
										disabled={!hasPermission || isAudioRecording}
										size="icon"
										className={`h-16 w-16 rounded-full border-4 border-white shadow-xl transition-all sm:h-20 sm:w-20 ${
											isRecording
												? "bg-red-500 hover:bg-red-600"
												: "bg-white hover:bg-gray-200"
										}`}
									>
										{isRecording ? (
											<>
												<div className="h-6 w-6 rounded-sm bg-white sm:hidden" />
												<div className="hidden h-8 w-8 rounded-sm bg-white sm:block" />
											</>
										) : (
											<>
												<Circle
													size={32}
													className="fill-red-500 text-red-500 sm:hidden"
												/>
												<Circle
													size={40}
													className="hidden fill-red-500 text-red-500 sm:block"
												/>
											</>
										)}
									</Button>
								)}
							</div>

							{/* Bottom bar with mode switcher and upload button */}
							<div className="flex w-full items-center justify-between gap-4 rounded-t-2xl border-t border-white/20 bg-black/60 px-4 py-3 backdrop-blur-sm">
								{/* Upload button */}
								<button
									onClick={() => {
										// Trigger appropriate input based on mode
										if (mode === "photo") {
											fileInputRef.current?.click();
										} else if (mode === "video") {
											videoInputRef.current?.click();
										} else if (mode === "audio") {
											audioInputRef.current?.click();
										}
									}}
									className="cursor-pointer transition-opacity hover:opacity-80"
									title="Upload file"
								>
									<img
										src={`${IMAGE_PATH}/newsfeed/upload.png`}
										alt="Upload"
										className="h-8 w-auto brightness-0 invert"
									/>
								</button>

								{/* Mode Toggle Buttons */}
								<div className="flex gap-2 rounded-full bg-black/40 p-1 backdrop-blur-sm">
									<Button
										onClick={() => setMode("photo")}
										disabled={isRecording || isAudioRecording}
										size="icon"
										className={`flex h-10 w-10 items-center justify-center rounded-full p-0 transition-all [&_svg]:!h-7 [&_svg]:!w-7 ${
											mode === "photo"
												? "bg-white text-black hover:bg-gray-200"
												: "bg-transparent text-white hover:bg-white/10"
										}`}
										title="Photo"
									>
										<CameraIcon size={28} />
									</Button>
									<Button
										onClick={() => setMode("video")}
										disabled={isRecording || isAudioRecording}
										size="icon"
										className={`flex h-10 w-10 items-center justify-center rounded-full p-0 transition-all [&_svg]:!h-7 [&_svg]:!w-7 ${
											mode === "video"
												? "bg-white text-black hover:bg-gray-200"
												: "bg-transparent text-white hover:bg-white/10"
										}`}
										title="Video"
									>
										<Video size={28} />
									</Button>
									<Button
										onClick={() => setMode("audio")}
										disabled={isRecording || isAudioRecording}
										size="icon"
										className={`flex h-10 w-10 items-center justify-center rounded-full p-0 transition-all [&_svg]:!h-7 [&_svg]:!w-7 ${
											mode === "audio"
												? "bg-white text-black hover:bg-gray-200"
												: "bg-transparent text-white hover:bg-white/10"
										}`}
										title="Audio"
									>
										<Mic size={28} />
									</Button>
								</div>

								{/* Spacer to maintain layout balance */}
								<div className="h-12 w-12" />
							</div>
						</div>
					) : null}
				</div>

				{/* Floating suggestions and specs overlay */}
				{(floatingSuggestions.length > 0 || floatingSpecs.length > 0) && (
					<>
						{/* Floating orange specs - behind everything */}
						<div
							className={`pointer-events-none absolute inset-0 z-[4] overflow-hidden ${hasUserInteracted ? "animate-fade-out-quickly" : ""}`}
						>
							{floatingSpecs.map((spec) => (
								<div
									key={spec.id}
									className={`absolute rounded-full opacity-70 ${
										spec.color === "green"
											? "bg-gradient-to-br from-green-400 to-emerald-500 shadow-[0_0_8px_rgba(74,222,128,0.6)]"
											: spec.color === "blue"
												? "bg-gradient-to-br from-blue-400 to-blue-600 shadow-[0_0_8px_rgba(59,130,246,0.6)]"
												: "bg-gradient-to-br from-orange-400 to-amber-500 shadow-[0_0_8px_rgba(251,146,60,0.6)]"
									}`}
									style={{
										left: `${spec.left}%`,
										bottom: "-20px",
										width: `${spec.size}px`,
										height: `${spec.size}px`,
										animation: `float-up-smooth 12s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
										animationDelay: `${spec.delay}s`,
									}}
								/>
							))}
						</div>

						{/* Floating suggestion bubbles - in front of specs */}
						<div
							className={`pointer-events-none absolute inset-0 z-[5] overflow-hidden ${hasUserInteracted ? "animate-fade-out-quickly" : ""}`}
						>
							{floatingSuggestions.map((suggestion) => (
								<div
									key={suggestion.id}
									className="absolute"
									style={{
										left: `calc(${suggestion.left}% - ${suggestion.left * 0.7}vw)`,
										bottom: "-60px",
										animation:
											"float-up-smooth 15s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
										width: "70vw",
										maxWidth: "280px",
									}}
								>
									<div
										className={`rounded-3xl border-2 px-3 py-2 text-center text-xs font-semibold leading-tight text-white backdrop-blur-sm sm:px-4 sm:py-2.5 sm:text-sm ${
											suggestion.isGrateful
												? "border-green-300/60 bg-gradient-to-br from-green-400/95 to-emerald-500/95 shadow-[0_8px_20px_rgba(74,222,128,0.5)]"
												: "border-orange-300/60 bg-gradient-to-br from-orange-400/95 to-amber-500/95 shadow-[0_8px_20px_rgba(251,146,60,0.5)]"
										}`}
										style={{
											textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
											wordWrap: "break-word",
											overflowWrap: "break-word",
										}}
									>
										{suggestion.text}
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</>
	);

	// Use portal to render camera at document.body level, ensuring it's above all Dialogs
	if (typeof document !== "undefined") {
		return createPortal(cameraContent, document.body);
	}
	return cameraContent;
};

export default Camera;
